import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast, Toaster } from '../ui/sonner';
import {
  ArrowLeft, Plus, Play, Pause, Trophy, Copy, Trash2, BarChart3,
  FlaskConical, Eye, MousePointerClick, UserCheck, TrendingUp,
  CheckCircle, XCircle, ChevronDown, ChevronUp, RefreshCw, Award,
  Settings, Zap, AlertTriangle, Clock, Shield, Brain, Timer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  running: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
};

const sigColors = {
  significant: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  not_significant: 'bg-slate-100 text-slate-600 border-slate-200',
  insufficient_data: 'bg-amber-100 text-amber-700 border-amber-300',
  inconclusive: 'bg-red-100 text-red-600 border-red-200',
};

const sigLabels = {
  significant: 'Significant',
  not_significant: 'Not Yet',
  insufficient_data: 'Low Data',
  inconclusive: 'Inconclusive',
};

// ─── Metric Card ───
const MetricCard = ({ icon: Icon, label, value, sub, color = 'emerald' }) => (
  <Card className="relative overflow-hidden" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold text-${color}-700 mt-1`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── Significance Badge ───
const SigBadge = ({ analysis }) => {
  if (!analysis) return null;
  const sig = analysis.significance || analysis;
  const status = sig.status || 'insufficient_data';
  return (
    <Badge className={`text-[10px] border ${sigColors[status]}`} data-testid="sig-badge">
      {sig.confidence > 0 ? `${sig.confidence}%` : sigLabels[status]}
    </Badge>
  );
};

// ─── Variant Bar ───
const VariantBar = ({ label, metrics, isWinner, sigData }) => {
  const impressions = metrics?.impressions || 0;
  const clicks = metrics?.clicks || 0;
  const formSubmits = metrics?.form_submits || 0;
  const downloads = metrics?.downloads || 0;
  const conversions = formSubmits + downloads;
  const convRate = impressions > 0 ? (conversions / impressions * 100).toFixed(1) : '0.0';
  const clickRate = impressions > 0 ? (clicks / impressions * 100).toFixed(1) : '0.0';

  const variantKey = label.toLowerCase();
  const rate = sigData ? (variantKey === 'a' ? sigData.rate_a : sigData.rate_b) : null;

  return (
    <div className={`p-4 rounded-xl border-2 ${isWinner ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 bg-gray-50/50'}`}
      data-testid={`variant-bar-${variantKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold px-2 py-0.5 rounded ${isWinner ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-700'}`}>
            {label}
          </span>
          {isWinner && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
              <Trophy className="w-3 h-3 mr-1" /> Winner
            </Badge>
          )}
        </div>
        <span className="text-lg font-bold text-gray-900">{rate !== null ? `${rate}%` : `${convRate}%`}</span>
      </div>
      <div className="grid grid-cols-4 gap-3 text-center">
        <div><p className="text-xs text-gray-500">Impressions</p><p className="text-sm font-semibold">{impressions.toLocaleString()}</p></div>
        <div><p className="text-xs text-gray-500">Clicks</p><p className="text-sm font-semibold">{clicks.toLocaleString()} ({clickRate}%)</p></div>
        <div><p className="text-xs text-gray-500">Leads</p><p className="text-sm font-semibold">{formSubmits.toLocaleString()}</p></div>
        <div><p className="text-xs text-gray-500">Downloads</p><p className="text-sm font-semibold">{downloads.toLocaleString()}</p></div>
      </div>
    </div>
  );
};

// ─── Experiment Row ───
const ExperimentRow = ({ exp, onAction, expandedId, setExpandedId, sigMap }) => {
  const isExpanded = expandedId === exp.experiment_id;
  const m = exp.metrics || {};
  const totalImpressions = (m.a?.impressions || 0) + (m.b?.impressions || 0);
  const totalConversions = (m.a?.form_submits || 0) + (m.b?.form_submits || 0) + (m.a?.downloads || 0) + (m.b?.downloads || 0);
  const overallConv = totalImpressions > 0 ? (totalConversions / totalImpressions * 100).toFixed(1) : '0.0';
  const analysis = sigMap[exp.experiment_id];
  const sig = analysis?.significance;
  const criteria = analysis?.completion_criteria;

  return (
    <Card className="mb-3" data-testid={`experiment-row-${exp.experiment_id}`}>
      <div className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={() => setExpandedId(isExpanded ? null : exp.experiment_id)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <FlaskConical className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 truncate" data-testid="experiment-name">{exp.experiment_name}</h3>
                {exp.auto_completed && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Auto</Badge>}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {exp.related_blog_slugs?.length || 0} blog(s) &middot; Split: {Math.round((exp.traffic_split || 0.5) * 100)}% / {Math.round((1 - (exp.traffic_split || 0.5)) * 100)}%
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 text-sm text-gray-600 mr-2">
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {totalImpressions.toLocaleString()}</span>
              <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> {totalConversions.toLocaleString()}</span>
              <span className="font-medium text-emerald-700">{overallConv}%</span>
            </div>
            {sig && <SigBadge analysis={analysis} />}
            <Badge className={`${statusColors[exp.status]} border text-xs`} data-testid="experiment-status">{exp.status}</Badge>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4" data-testid="experiment-expanded">
          {/* Significance Summary */}
          {sig && sig.sufficient_data && (
            <div className={`p-3 rounded-lg mb-4 border ${sig.is_significant ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}
              data-testid="significance-summary">
              <div className="flex items-center gap-3 flex-wrap">
                <Brain className={`w-4 h-4 ${sig.is_significant ? 'text-emerald-600' : 'text-gray-500'}`} />
                <span className="text-sm font-medium">{sig.reason}</span>
                <div className="flex items-center gap-4 text-xs text-gray-600 ml-auto">
                  <span>Z: {sig.z_score}</span>
                  <span>p: {sig.p_value}</span>
                  <span>Uplift: <strong className={sig.uplift > 0 ? 'text-emerald-700' : 'text-red-600'}>{sig.uplift > 0 ? '+' : ''}{sig.uplift}%</strong></span>
                </div>
              </div>
              {criteria && (
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {criteria.checks.map((ch, i) => (
                    <span key={ch.label || `check-${i}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${ch.passed ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                      {ch.passed ? '✓' : '○'} {ch.rule}
                    </span>
                  ))}
                  {criteria.ready && (
                    <Badge className="bg-emerald-600 text-white text-[10px] ml-auto">Ready to Complete</Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {sig && !sig.sufficient_data && (
            <div className="p-3 rounded-lg mb-4 bg-amber-50 border border-amber-200 flex items-center gap-2 text-sm text-amber-700" data-testid="insufficient-data-notice">
              <AlertTriangle className="w-4 h-4" />
              <span>{sig.reason}</span>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <VariantBar label="A" metrics={m.a} isWinner={exp.winner === 'a'} sigData={sig} />
            <VariantBar label="B" metrics={m.b} isWinner={exp.winner === 'b'} sigData={sig} />
          </div>

          {/* Variant Details */}
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            {['a', 'b'].map(v => {
              const vd = exp[`variant_${v}`] || {};
              return (
                <div key={v} className="p-3 rounded-lg bg-white border border-gray-200 text-sm" data-testid={`variant-detail-${v}`}>
                  <p className="font-semibold text-gray-700 mb-1">Variant {v.toUpperCase()} Config</p>
                  <p className="text-gray-600"><span className="text-gray-400">Headline:</span> {vd.cta_headline || '—'}</p>
                  <p className="text-gray-600"><span className="text-gray-400">Button:</span> {vd.button_text || '—'}</p>
                  <p className="text-gray-600"><span className="text-gray-400">Gate:</span> {vd.email_gate_enabled ? 'Gated' : 'Ungated'}</p>
                  <p className="text-gray-600"><span className="text-gray-400">Placement:</span> {vd.placement || 'end'}</p>
                  <p className="text-gray-600"><span className="text-gray-400">Magnet:</span> {vd.lead_magnet_slug || '—'}</p>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {exp.status === 'draft' && (
              <Button size="sm" onClick={() => onAction('start', exp.experiment_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-experiment-btn">
                <Play className="w-3.5 h-3.5 mr-1" /> Start
              </Button>
            )}
            {exp.status === 'running' && (
              <>
                <Button size="sm" variant="outline" onClick={() => onAction('pause', exp.experiment_id)} data-testid="pause-experiment-btn">
                  <Pause className="w-3.5 h-3.5 mr-1" /> Pause
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => onAction('complete', exp.experiment_id)} data-testid="complete-experiment-btn">
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Complete
                </Button>
              </>
            )}
            {exp.status === 'paused' && (
              <Button size="sm" onClick={() => onAction('start', exp.experiment_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="resume-experiment-btn">
                <Play className="w-3.5 h-3.5 mr-1" /> Resume
              </Button>
            )}
            {exp.status === 'completed' && exp.winner && !exp.winner_applied && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => onAction('apply-winner', exp.experiment_id)} data-testid="apply-winner-btn">
                <Award className="w-3.5 h-3.5 mr-1" /> Apply Winner ({exp.winner.toUpperCase()})
              </Button>
            )}
            {exp.winner_applied && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Winner Applied</Badge>}
            <Button size="sm" variant="outline" onClick={() => onAction('duplicate', exp.experiment_id)} data-testid="duplicate-experiment-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => onAction('delete', exp.experiment_id)} data-testid="delete-experiment-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// ─── Scheduler Settings Panel ───
const SchedulerPanel = ({ config, onSave, onCheckNow, checking }) => {
  if (!config) return null;
  return (
    <Card data-testid="scheduler-settings-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2"><Timer className="w-4 h-4 text-blue-600" /> Auto-Completion Scheduler</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${config.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
            <span className="text-sm font-medium">{config.enabled ? 'Active' : 'Disabled'}</span>
            {config.auto_complete && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Auto-Complete</Badge>}
            {config.auto_apply_winner && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Auto-Apply</Badge>}
            {!config.auto_complete && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Recommend Only</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onCheckNow} disabled={checking} data-testid="check-now-btn">
              {checking ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              Check Now
            </Button>
            <Button size="sm" variant="outline" onClick={() => onSave({ enabled: !config.enabled })} data-testid="toggle-ab-scheduler-btn">
              {config.enabled ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {config.enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-medium text-gray-500 mb-1 block">Confidence Target</label>
            <select className="w-full h-8 rounded-md border border-gray-300 px-2 text-xs bg-white" value={config.target_confidence || 95}
              onChange={e => onSave({ target_confidence: parseFloat(e.target.value) })} data-testid="target-confidence-select">
              <option value="90">90%</option><option value="95">95%</option><option value="99">99%</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-500 mb-1 block">Min Impressions</label>
            <Input type="number" className="h-8 text-xs" value={config.min_impressions_per_variant || 100}
              onChange={e => onSave({ min_impressions_per_variant: parseInt(e.target.value) || 100 })} data-testid="min-impressions-input" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-500 mb-1 block">Min Runtime (days)</label>
            <Input type="number" className="h-8 text-xs" value={config.min_runtime_days || 3}
              onChange={e => onSave({ min_runtime_days: parseInt(e.target.value) || 3 })} data-testid="min-runtime-input" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-gray-500 mb-1 block">Check Interval (min)</label>
            <Input type="number" className="h-8 text-xs" value={config.check_interval_minutes || 60}
              onChange={e => onSave({ check_interval_minutes: parseInt(e.target.value) || 60 })} data-testid="check-interval-input" />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.auto_complete ?? false}
              onChange={e => onSave({ auto_complete: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" data-testid="auto-complete-check" /> Auto-complete experiments
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.auto_apply_winner ?? false}
              onChange={e => onSave({ auto_apply_winner: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" data-testid="auto-apply-check" /> Auto-apply winner
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.notify_on_significance ?? true}
              onChange={e => onSave({ notify_on_significance: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" data-testid="notify-sig-check" /> Notify on significance
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.notify_on_auto_complete ?? true}
              onChange={e => onSave({ notify_on_auto_complete: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600" data-testid="notify-auto-check" /> Notify on auto-complete
          </label>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Reporting Panel ───
const ReportingPanel = ({ reporting }) => {
  if (!reporting) return null;
  const { top_winning_variants, running_analyses, auto_completed_experiments, inconclusive_experiments } = reporting;

  return (
    <div className="space-y-4" data-testid="reporting-panel">
      {/* Top Winning CTA Variants */}
      {top_winning_variants?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Top Winning CTA Variants</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500">Experiment</th>
                  <th className="text-left py-2 text-gray-500">Headline</th>
                  <th className="text-center py-2 text-gray-500">Button</th>
                  <th className="text-center py-2 text-gray-500">Gate</th>
                  <th className="text-right py-2 text-gray-500">Conv Rate</th>
                  <th className="text-right py-2 text-gray-500">Status</th>
                </tr></thead>
                <tbody>
                  {top_winning_variants.map((w, i) => (
                    <tr key={wv.experiment || `wv-${i}`} className="border-b border-gray-50">
                      <td className="py-2 text-gray-800">{w.experiment}</td>
                      <td className="py-2 text-gray-600 max-w-[200px] truncate">{w.headline}</td>
                      <td className="py-2 text-center text-gray-600">{w.button_text}</td>
                      <td className="py-2 text-center"><Badge variant="outline" className="text-[10px]">{w.gate}</Badge></td>
                      <td className="py-2 text-right font-semibold text-emerald-700">{w.rate}%</td>
                      <td className="py-2 text-right">
                        {w.auto_completed && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Auto</Badge>}
                        {w.winner_applied && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-1">Applied</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Running Experiment Significance */}
      {running_analyses?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Running Experiment Health</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {running_analyses.map((a, i) => {
                const s = a.significance;
                const c = a.completion_criteria;
                return (
                  <div key={s.experiment_name || `sig-${i}`} className={`p-3 rounded-lg border ${s.is_significant ? 'bg-emerald-50/50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}
                    data-testid={`running-health-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-sm font-medium text-gray-800">{a.experiment_name}</span>
                      <div className="flex items-center gap-2">
                        <SigBadge analysis={a} />
                        <span className="text-xs text-gray-500">{s.rate_a}% vs {s.rate_b}%</span>
                        <span className={`text-xs font-medium ${s.uplift > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {s.uplift > 0 ? '+' : ''}{s.uplift}%
                        </span>
                        {c?.ready && <Badge className="bg-emerald-600 text-white text-[10px]">Ready</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto-completed + Inconclusive */}
      {(auto_completed_experiments?.length > 0 || inconclusive_experiments?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {auto_completed_experiments?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-purple-600" /> Auto-Completed</CardTitle></CardHeader>
              <CardContent>
                {auto_completed_experiments.map((e, i) => (
                  <div key={`detail-${i}`} className="text-xs py-1 flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>{e.experiment_name}</span>
                    <span className="text-gray-400 ml-auto">Winner: {e.winner?.toUpperCase()}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {inconclusive_experiments?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Inconclusive</CardTitle></CardHeader>
              <CardContent>
                {inconclusive_experiments.map((e, i) => (
                  <div key={`detail-b-${i}`} className="text-xs py-1 flex items-center gap-2">
                    <XCircle className="w-3 h-3 text-amber-500" />
                    <span>{e.experiment_name}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Create Experiment Form ───
const CreateExperimentForm = ({ onClose, onCreated, blogs }) => {
  const [form, setForm] = useState({
    experiment_name: '', related_blog_slugs: [], traffic_split: 0.5,
    variant_a: { cta_headline: '', cta_description: '', button_text: 'Download Free PDF', lead_magnet_slug: '', lead_magnet_title: '', lead_magnet_type: '', email_gate_enabled: true, placement: 'end', thank_you_message: 'Thank you! Your download is ready.' },
    variant_b: { cta_headline: '', cta_description: '', button_text: 'Download Free PDF', lead_magnet_slug: '', lead_magnet_title: '', lead_magnet_type: '', email_gate_enabled: true, placement: 'end', thank_you_message: 'Thank you! Your download is ready.' },
  });
  const [saving, setSaving] = useState(false);
  const [blogSearch, setBlogSearch] = useState('');

  const updateVariant = (v, field, value) => setForm(f => ({ ...f, [`variant_${v}`]: { ...f[`variant_${v}`], [field]: value } }));
  const toggleBlog = (slug) => setForm(f => ({ ...f, related_blog_slugs: f.related_blog_slugs.includes(slug) ? f.related_blog_slugs.filter(s => s !== slug) : [...f.related_blog_slugs, slug] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.experiment_name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/ab-tests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { toast.success('Experiment created'); onCreated(); } else { toast.error('Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const filteredBlogs = blogs.filter(b => b.title?.toLowerCase().includes(blogSearch.toLowerCase()) || b.slug?.toLowerCase().includes(blogSearch.toLowerCase())).slice(0, 10);

  const VariantFields = ({ v, label }) => (
    <div className="space-y-3 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
      <h4 className="font-semibold text-gray-800">Variant {label}</h4>
      <Input placeholder="CTA Headline" value={form[`variant_${v}`].cta_headline} onChange={e => updateVariant(v, 'cta_headline', e.target.value)} data-testid={`variant-${v}-headline`} />
      <Input placeholder="CTA Description" value={form[`variant_${v}`].cta_description} onChange={e => updateVariant(v, 'cta_description', e.target.value)} data-testid={`variant-${v}-description`} />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Button Text" value={form[`variant_${v}`].button_text} onChange={e => updateVariant(v, 'button_text', e.target.value)} data-testid={`variant-${v}-button`} />
        <Input placeholder="Lead Magnet Slug" value={form[`variant_${v}`].lead_magnet_slug} onChange={e => updateVariant(v, 'lead_magnet_slug', e.target.value)} data-testid={`variant-${v}-slug`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white" value={form[`variant_${v}`].email_gate_enabled ? 'true' : 'false'}
          onChange={e => updateVariant(v, 'email_gate_enabled', e.target.value === 'true')} data-testid={`variant-${v}-gate`}>
          <option value="true">Gated (email required)</option><option value="false">Ungated</option>
        </select>
        <select className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white" value={form[`variant_${v}`].placement}
          onChange={e => updateVariant(v, 'placement', e.target.value)} data-testid={`variant-${v}-placement`}>
          <option value="end">End of post</option><option value="inline">Inline</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-experiment-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Create A/B Experiment</h2>
          <p className="text-sm text-gray-500 mt-1">Define two variants to test against each other</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div><label className="text-sm font-medium text-gray-700 mb-1 block">Experiment Name</label>
            <Input placeholder="e.g. GST Checklist: Headline A vs B" value={form.experiment_name} onChange={e => setForm(f => ({ ...f, experiment_name: e.target.value }))} data-testid="experiment-name-input" /></div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Traffic Split (A%)</label>
            <div className="flex items-center gap-3">
              <input type="range" min="10" max="90" step="5" value={form.traffic_split * 100}
                onChange={e => setForm(f => ({ ...f, traffic_split: parseInt(e.target.value) / 100 }))} className="flex-1 accent-emerald-600" data-testid="traffic-split-slider" />
              <span className="text-sm font-medium text-gray-700 w-24 text-center">{Math.round(form.traffic_split * 100)}% / {Math.round((1 - form.traffic_split) * 100)}%</span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Target Blogs</label>
            <Input placeholder="Search blogs..." value={blogSearch} onChange={e => setBlogSearch(e.target.value)} data-testid="blog-search-input" />
            {blogSearch && <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg">{filteredBlogs.map(b => (
              <button type="button" key={b.slug} className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${form.related_blog_slugs.includes(b.slug) ? 'bg-emerald-50 text-emerald-700' : ''}`}
                onClick={() => toggleBlog(b.slug)}>{form.related_blog_slugs.includes(b.slug) ? '+ ' : ''}{b.title}</button>
            ))}</div>}
            {form.related_blog_slugs.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{form.related_blog_slugs.map(s => (
              <Badge key={s} variant="outline" className="text-xs cursor-pointer" onClick={() => toggleBlog(s)}>{s} x</Badge>
            ))}</div>}
          </div>
          <div className="grid md:grid-cols-2 gap-4"><VariantFields v="a" label="A (Control)" /><VariantFields v="b" label="B (Challenger)" /></div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-create-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-experiment-btn">{saving ? 'Creating...' : 'Create Experiment'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───
const ABTestingDashboard = () => {
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [blogs, setBlogs] = useState([]);
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [reporting, setReporting] = useState(null);
  const [showReporting, setShowReporting] = useState(false);
  const [sigMap, setSigMap] = useState({});
  const [checking, setChecking] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, blogsRes, configRes, sigRes, reportRes] = await Promise.all([
        fetch(`${API}/api/admin/ab-tests/analytics/summary`),
        fetch(`${API}/api/blogs/?limit=200&status=published`),
        fetch(`${API}/api/admin/ab-tests/scheduler/config`),
        fetch(`${API}/api/admin/ab-tests/significance-all`),
        fetch(`${API}/api/admin/ab-tests/reporting`),
      ]);
      if (summaryRes.ok) { const d = await summaryRes.json(); setSummary(d); setExperiments(d.experiments || []); }
      if (blogsRes.ok) { const d = await blogsRes.json(); setBlogs(d.blogs || []); }
      if (configRes.ok) setSchedulerConfig(await configRes.json());
      if (sigRes.ok) {
        const d = await sigRes.json();
        const map = {};
        (d.analyses || []).forEach(a => { map[a.experiment_id] = a; });
        setSigMap(map);
      }
      if (reportRes.ok) setReporting(await reportRes.json());
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (action, experimentId) => {
    if (action === 'delete' && !window.confirm('Delete this experiment?')) return;
    const urlMap = {
      start: `${API}/api/admin/ab-tests/${experimentId}/start`, pause: `${API}/api/admin/ab-tests/${experimentId}/pause`,
      complete: `${API}/api/admin/ab-tests/${experimentId}/complete`, duplicate: `${API}/api/admin/ab-tests/${experimentId}/duplicate`,
      delete: `${API}/api/admin/ab-tests/${experimentId}`, 'apply-winner': `${API}/api/admin/ab-tests/${experimentId}/apply-winner`,
    };
    try {
      const res = await fetch(urlMap[action], { method: action === 'delete' ? 'DELETE' : 'POST' });
      if (res.ok) { toast.success(`Action complete`); fetchData(); } else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const saveSchedulerConfig = async (updates) => {
    try {
      const res = await fetch(`${API}/api/admin/ab-tests/scheduler/config`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      if (res.ok) { const d = await res.json(); setSchedulerConfig(d.config); toast.success('Settings saved'); } else { toast.error('Save failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API}/api/admin/ab-tests/scheduler/check-now`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); toast.success(`Checked ${d.total_checked} experiments. ${d.significant?.length || 0} significant, ${d.auto_completed?.length || 0} auto-completed`); fetchData(); }
    } catch { toast.error('Failed'); }
    setChecking(false);
  };

  const handleSeed = async () => {
    try {
      const res = await fetch(`${API}/api/admin/ab-tests/seed`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message);
      fetchData();
    } catch { toast.error('Seed failed'); }
  };

  const filtered = statusFilter === 'all' ? experiments : experiments.filter(e => e.status === statusFilter);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ab-testing-dashboard">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin-btn"><ArrowLeft className="w-4 h-4" /></Button>
              <div><h1 className="text-lg font-bold text-gray-900">A/B Testing</h1><p className="text-xs text-gray-500">Lead Magnet CTA Experiments</p></div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowReporting(!showReporting)} data-testid="toggle-reporting-btn">
                <BarChart3 className="w-4 h-4 mr-1" /> {showReporting ? 'Hide' : 'Show'} Reports
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowScheduler(!showScheduler)} data-testid="toggle-scheduler-btn">
                <Settings className="w-4 h-4 mr-1" /> Scheduler
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
              <Button size="sm" onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-experiment-btn">
                <Plus className="w-4 h-4 mr-1" /> New Experiment
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
        ) : (
          <>
            {/* Summary Metrics */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="summary-metrics">
                <MetricCard icon={FlaskConical} label="Experiments" value={summary.total_experiments} sub={`${summary.active} active`} />
                <MetricCard icon={Eye} label="Impressions" value={summary.total_impressions.toLocaleString()} color="blue" />
                <MetricCard icon={UserCheck} label="Conversions" value={summary.total_conversions.toLocaleString()} color="purple" />
                <MetricCard icon={TrendingUp} label="Avg Uplift" value={`${summary.avg_conversion_uplift}%`} sub={`${summary.overall_conversion_rate}% conv.`} color="amber" />
                <MetricCard icon={Zap} label="Auto-Completed" value={summary.auto_completed_count || 0} sub={`${summary.inconclusive_count || 0} inconclusive`} color="purple" />
              </div>
            )}

            {/* Scheduler Settings */}
            {showScheduler && (
              <div className="mb-6">
                <SchedulerPanel config={schedulerConfig} onSave={saveSchedulerConfig} onCheckNow={handleCheckNow} checking={checking} />
              </div>
            )}

            {/* Reporting */}
            {showReporting && (
              <div className="mb-6">
                <ReportingPanel reporting={reporting} />
              </div>
            )}

            {/* Magnet Type Performance */}
            {summary?.by_magnet_type?.length > 0 && (
              <Card className="mb-6" data-testid="magnet-type-performance">
                <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-gray-700">Performance by Magnet Type</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-100">
                        <th className="text-left py-2 text-gray-500 font-medium">Type</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Experiments</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Impressions</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Conversions</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Conv Rate</th>
                      </tr></thead>
                      <tbody>{summary.by_magnet_type.map(t => (
                        <tr key={t.type} className="border-b border-gray-50">
                          <td className="py-2 font-medium text-gray-800 capitalize">{t.type?.replace(/_/g, ' ')}</td>
                          <td className="py-2 text-right text-gray-600">{t.experiments}</td>
                          <td className="py-2 text-right text-gray-600">{t.impressions.toLocaleString()}</td>
                          <td className="py-2 text-right text-gray-600">{t.conversions.toLocaleString()}</td>
                          <td className="py-2 text-right font-semibold text-emerald-700">{t.conv_rate}%</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filter + List */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-1 bg-white rounded-lg border border-gray-200 p-1" data-testid="status-filter">
                {['all', 'running', 'draft', 'paused', 'completed'].map(s => (
                  <button key={s} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${statusFilter === s ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}{s !== 'all' && ` (${experiments.filter(e => e.status === s).length})`}
                  </button>
                ))}
              </div>
              {experiments.length === 0 && <Button size="sm" variant="outline" onClick={handleSeed} data-testid="seed-btn">Seed Demo Data</Button>}
            </div>

            {filtered.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No experiments {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'yet'}</p>
                <div className="flex justify-center gap-2 mt-4">
                  <Button size="sm" onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="empty-create-btn"><Plus className="w-4 h-4 mr-1" /> Create</Button>
                  {experiments.length === 0 && <Button size="sm" variant="outline" onClick={handleSeed} data-testid="empty-seed-btn">Load Demo</Button>}
                </div>
              </CardContent></Card>
            ) : (
              <div data-testid="experiments-list">
                {filtered.map(exp => (
                  <ExperimentRow key={exp.experiment_id} exp={exp} onAction={handleAction} expandedId={expandedId} setExpandedId={setExpandedId} sigMap={sigMap} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && <CreateExperimentForm onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} blogs={blogs} />}
      <Toaster />
    </div>
  );
};

export default ABTestingDashboard;
